import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "set-up-component"
    }}>{`Set up component`}</h1>
    <p>{`Setting up your component is about getting the essential component files in place so that your component is actively participating in the Page Builder component framework. After you create these files and wire them into the component framework, you can focus all your attention on developing each file to faithfully recreate your content type as a PWA component. The following steps highlight the tasks for setting up your component:`}</p>
    <p><img parentName="p" {...{
        "src": "/commerce/pwa-studio/assets/947d6a2de7d959ebc296251141d1059a/SetupComponentSteps.svg",
        "alt": "Component setup steps"
      }}></img></p>
    <h2 {...{
      "id": "step-1-create-starter-files"
    }}>{`Step 1: Create starter files`}</h2>
    <p>{`The first thing you need to do within PWA Studio is create the directory and skeleton files needed for your component, as follows:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`configAggregator.js`}</strong>{` (required) - Function to retrieve content and styling properties from your content type's master format HTML.`}</li>
      <li parentName="ul"><strong parentName="li">{`myComponent.js`}</strong>{` (required) - React component for populating properties that correspond to the original Page Builder content type. For our ExampleQuote, we use `}<inlineCode parentName="li">{`exampleQuote.js`}</inlineCode>{`.`}</li>
      <li parentName="ul"><strong parentName="li">{`myComponent.css`}</strong>{` (optional but usually needed) - CSS classes that provide addition styles needed to present your component as desired. For our ExampleQuote, we use `}<inlineCode parentName="li">{`exampleQuote.css`}</inlineCode>{`.`}</li>
      <li parentName="ul"><strong parentName="li">{`index.js`}</strong>{` (required) - Entry file needed to identify your component in PWA Studio.`}</li>
    </ul>
    <p>{`Currently, you need to add your content type's component, like `}<inlineCode parentName="p">{`ExampleQuote`}</inlineCode>{`, to the `}<inlineCode parentName="p">{`ContentTypes`}</inlineCode>{` directory, where the native Page Builder components are kept. This will change when we implement different rules for extending Page Builder components. But for now, add your custom content type components as shown here:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "606px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "58.4375%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/pwa-studio/static/6621ec5b70d3e49bf8b43f4ad435bc78/5530d/PageBuilderFilesSetup.webp 320w", "/commerce/pwa-studio/static/6621ec5b70d3e49bf8b43f4ad435bc78/e5c51/PageBuilderFilesSetup.webp 606w"],
            "sizes": "(max-width: 606px) 100vw, 606px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/pwa-studio/static/6621ec5b70d3e49bf8b43f4ad435bc78/dd4a7/PageBuilderFilesSetup.png 320w", "/commerce/pwa-studio/static/6621ec5b70d3e49bf8b43f4ad435bc78/cec09/PageBuilderFilesSetup.png 606w"],
            "sizes": "(max-width: 606px) 100vw, 606px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/pwa-studio/static/6621ec5b70d3e49bf8b43f4ad435bc78/cec09/PageBuilderFilesSetup.png",
            "alt": "Component file structure",
            "title": "Component file structure",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`Starter code for the `}<inlineCode parentName="p">{`ExampleQuote`}</inlineCode>{` component files is provided as follows.`}</p>
    <h3 {...{
      "id": "configaggregatorjs"
    }}>{`configAggregator.js`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`export default (node, props) => {
  return {
    // Retrieve properties from node here
  };
};
`}</code></pre>
    <h3 {...{
      "id": "examplequotecss"
    }}>{`exampleQuote.css`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-css"
      }}>{`/* class for testing setup */
.testClass {
  line-height: 1.4em;
  font-size: 1em;
  color: #0066cc;
}
`}</code></pre>
    <h3 {...{
      "id": "examplequotejs"
    }}>{`exampleQuote.js`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from "react";
import { mergeClasses } from "../../../../../classify";
import defaultClasses from "./exampleQuote.css";
import { shape, string } from "prop-types";

// Component for testing setup
const ExampleQuote = (props) => {
  const classes = mergeClasses(defaultClasses, props.classes);
  return (
    <div className={classes.testClass}>
      <div>Content Type: {props.contentType}</div>
      <div>Appearance: {props.appearance}</div>
    </div>
  );
};

ExampleQuote.propTypes = {
  classes: shape({
    testClass: string,
  }),
  contentType: string,
  appearance: string,
};

export default ExampleQuote;
`}</code></pre>
    <h3 {...{
      "id": "indexjs"
    }}>{`index.js`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`export { default } from "./exampleQuote";
`}</code></pre>
    <h2 {...{
      "id": "step-2-configure-component"
    }}>{`Step 2: Configure component`}</h2>
    <p>{`Configuring your component is about connecting your component to the Page Builder component framework. The framework provides a static config object (in `}<inlineCode parentName="p">{`config.js`}</inlineCode>{`) that acts as a kind of registry of content types supported within PWA Studio. To include support for your custom content type component, you'll need to add an entry to the `}<inlineCode parentName="p">{`config.js`}</inlineCode>{`:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "606px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "20.3125%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/pwa-studio/static/326618482ec26eb6b46af06f83617523/5530d/PageBuilderConfigFile.webp 320w", "/commerce/pwa-studio/static/326618482ec26eb6b46af06f83617523/e5c51/PageBuilderConfigFile.webp 606w"],
            "sizes": "(max-width: 606px) 100vw, 606px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/pwa-studio/static/326618482ec26eb6b46af06f83617523/dd4a7/PageBuilderConfigFile.png 320w", "/commerce/pwa-studio/static/326618482ec26eb6b46af06f83617523/cec09/PageBuilderConfigFile.png 606w"],
            "sizes": "(max-width: 606px) 100vw, 606px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/pwa-studio/static/326618482ec26eb6b46af06f83617523/cec09/PageBuilderConfigFile.png",
            "alt": "Component config file",
            "title": "Component config file",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`Open the `}<inlineCode parentName="p">{`config.js`}</inlineCode>{` file and `}<inlineCode parentName="p">{`import`}</inlineCode>{` your `}<inlineCode parentName="p">{`configAggregator`}</inlineCode>{`. The import statement for our ExampleQuote is shown here:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import exampleQuoteConfigAggregator from "./ContentTypes/ExampleQuote/configAggregator";
`}</code></pre>
    <p>{`Add an entry that references your `}<inlineCode parentName="p">{`configAggregator`}</inlineCode>{` and `}<inlineCode parentName="p">{`component`}</inlineCode>{`, using the name of your content type as the `}<inlineCode parentName="p">{`key`}</inlineCode>{`. For our `}<inlineCode parentName="p">{`ExampleQuote`}</inlineCode>{`, the name of the Quote content type is `}<inlineCode parentName="p">{`example_quote`}</inlineCode>{`, so the entry for our Quote component (`}<inlineCode parentName="p">{`ExampleQuote`}</inlineCode>{`) in the config file looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`Quote_Custom: {
    configAggregator: exampleQuoteConfigAggregator,
    component: React.lazy(() => import('./ContentTypes/ExampleQuote'))
},
`}</code></pre>
    <h3 {...{
      "id": "a-note-about-lazy-loading-components"
    }}>{`A note about lazy loading components`}</h3>
    <p>{`When building PWAs, performance is key. That's why we recommend loading less critical components using `}<inlineCode parentName="p">{`React.lazy`}</inlineCode>{`. This will result in a very slight delay in that content rendering but will ensure we don't bloat the bundle size for the store. For more information, see the `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/code-splitting.html#reactlazy"
      }}>{`react lazy loading`}</a>{` docs on the React site.`}</p>
    <p>{`By default, we apply `}<inlineCode parentName="p">{`React.lazy`}</inlineCode>{` to the following content types: Banner, Slider, Slide Item, Tab, Tab Item, Button, Button Item, Block, Products, HTML, and Divider.`}</p>
    <p>{`To have your component loaded lazily you simply have to wrap your `}<inlineCode parentName="p">{`component`}</inlineCode>{` reference in a call to `}<inlineCode parentName="p">{`React.lazy`}</inlineCode>{` as previously shown for the ExampleQuote component:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`component: React.lazy(() => import("./ContentTypes/ExampleQuote"));
`}</code></pre>
    <h2 {...{
      "id": "test-the-component-setup"
    }}>{`Test the component setup`}</h2>
    <p>{`After adding these files and hooking them up to the framework through the configuration file (`}<inlineCode parentName="p">{`config.js`}</inlineCode>{`), your skeleton component will appear on the Venia home page:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "650px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "28.750000000000004%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/pwa-studio/static/759e9690d2efa34cbbdb9af9923f3b0c/5530d/ConfigureComponentOutput.webp 320w", "/commerce/pwa-studio/static/759e9690d2efa34cbbdb9af9923f3b0c/0c8fb/ConfigureComponentOutput.webp 640w", "/commerce/pwa-studio/static/759e9690d2efa34cbbdb9af9923f3b0c/309d5/ConfigureComponentOutput.webp 650w"],
            "sizes": "(max-width: 650px) 100vw, 650px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/pwa-studio/static/759e9690d2efa34cbbdb9af9923f3b0c/dd4a7/ConfigureComponentOutput.png 320w", "/commerce/pwa-studio/static/759e9690d2efa34cbbdb9af9923f3b0c/0f09e/ConfigureComponentOutput.png 640w", "/commerce/pwa-studio/static/759e9690d2efa34cbbdb9af9923f3b0c/663f3/ConfigureComponentOutput.png 650w"],
            "sizes": "(max-width: 650px) 100vw, 650px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/pwa-studio/static/759e9690d2efa34cbbdb9af9923f3b0c/663f3/ConfigureComponentOutput.png",
            "alt": "ExampleQuote component rendered with starter code",
            "title": "ExampleQuote component rendered with starter code",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`If you see this rendering without errors in the Venia app, you are ready to start developing your content type component.`}</p>
    <h2 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h2>
    <p>{`If you have errors, make sure you have:`}</p>
    <ol>
      <li parentName="ol">{`Copied the correct starter code for each of the four files.`}</li>
      <li parentName="ol">{`Added the correct name of your content type to the `}<inlineCode parentName="li">{`config.js`}</inlineCode>{` file (`}<inlineCode parentName="li">{`Quote_Custom`}</inlineCode>{` if you are using the Quote content type).`}</li>
    </ol>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      